import { useEffect, useRef, useState } from "react";
import { createChart } from "lightweight-charts";
import { api } from "../api";
import { useLang } from "../i18n";

export const PriceChart = ({ system = "alpha", title }) => {
  const { lang, t } = useLang();
  const ref = useRef(null);
  const [data, setData] = useState({ prices: [], trades: [] });
  const guardian = system === "guardian";
  const upColor = guardian ? "#38BDF8" : "#10B981";

  useEffect(() => {
    const tradesReq = guardian
      ? api.get("/guardian/markers").then((r) => r.data.markers)
      : api.get("/trades").then((r) => r.data.trades);
    Promise.all([api.get("/public/prices", { params: { limit: 400 } }), tradesReq])
      .then(([p, trades]) => setData({ prices: p.data, trades })).catch(() => {});
  }, [guardian]);

  useEffect(() => {
    if (!ref.current || !data.prices.length) return;
    const chart = createChart(ref.current, {
      height: 380,
      layout: { background: { color: guardian ? "#0B1220" : "#111111" }, textColor: "#94A3B8",
        fontFamily: "IBM Plex Mono, monospace", fontSize: 11 },
      grid: { vertLines: { color: guardian ? "#16233B" : "#1f1f1f" }, horzLines: { color: guardian ? "#16233B" : "#1f1f1f" } },
      rightPriceScale: { borderColor: "#262626" },
      timeScale: { borderColor: "#262626" },
      crosshair: { horzLine: { color: "#475569" }, vertLine: { color: "#475569" } },
      localization: { locale: lang === "it" ? "it-IT" : "en-GB" },
    });
    const series = chart.addCandlestickSeries({
      upColor, downColor: "#EF4444", borderVisible: false,
      wickUpColor: upColor, wickDownColor: "#EF4444",
    });
    series.setData(data.prices.map((p) => ({
      time: p.date, open: p.open, high: p.high, low: p.low, close: p.close })));
    const firstDate = data.prices[0].date;
    const markers = [];
    for (const tr of data.trades) {
      if (!tr.rollover && tr.open_date >= firstDate) {
        markers.push({
          time: tr.open_date,
          position: tr.direction === "LONG" ? "belowBar" : "aboveBar",
          color: tr.direction === "LONG" ? upColor : "#EF4444",
          shape: tr.direction === "LONG" ? "arrowUp" : "arrowDown",
          text: `${tr.direction} ${tr.open_price}`,
        });
      }
      if (tr.close_date && !tr.closed_by_rollover && tr.close_date >= firstDate) {
        markers.push({
          time: tr.close_date, position: "inBar", color: "#E2E8F0",
          shape: "circle", text: `EXIT ${tr.close_price}`,
        });
      }
    }
    markers.sort((a, b) => (a.time < b.time ? -1 : a.time > b.time ? 1 : 0));
    series.setMarkers(markers);
    chart.timeScale().setVisibleLogicalRange({
      from: Math.max(0, data.prices.length - 130), to: data.prices.length });
    const resize = () => chart.applyOptions({ width: ref.current?.clientWidth || 600 });
    resize();
    window.addEventListener("resize", resize);
    return () => { window.removeEventListener("resize", resize); chart.remove(); };
  }, [data, lang, guardian, upColor]);

  if (!data.prices.length) return null;
  return (
    <div data-testid={guardian ? "guardian-price-chart" : "price-chart"}
      className={`border p-4 ${guardian ? "border-sky-500/20 bg-[#0B1220]" : "border-white/10 bg-[#111111]"}`}>
      <h3 className="text-sm font-medium text-slate-200 mb-4">{title || t("price_chart_title")}</h3>
      <div ref={ref} className="w-full" />
    </div>
  );
};
